import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, Grid, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import CommonSubHeader from 'components/commonSubHeader/CommonSubHeader';
import BillingFlow from './billingFlow/BillingFlow';
import { biillingTabs } from './BillingData';
import { getDateFormat } from 'utils/utils';
import {
  clearBillingList,
  clearFinalInvoice,
  getBillingList,
  getFinalInvoice,
  getBillSelfEligibleInvoices,
  getBillSelfConsolidatedInvoicesList,
} from 'redux/actions/BillingAction';

const Billing = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const params = Object.fromEntries([...searchParams]);

  const companyId = params?.companyId;
  const companyName = params?.companyName;
  const city = params?.cityId;
  const cityName = params?.cityName;
  const status = params?.status;
  const pageValue = params?.page;
  const pageRowSize = params?.pageRowSize;
  const fromDate = params?.startDate;
  const toDate = params?.endDate;
  const employeeId = params?.employeeId;

  const [value, setValue] = useState(params?.billingType || 'billToCompany');
  const [dateFrom, setDateFrom] = useState(
    fromDate === 'noSelectedDate'
      ? ''
      : fromDate
        ? fromDate
        : getDateFormat(new Date(), 'reservationDateFormat'),
  );
  const [dateTo, setDateTo] = useState(
    toDate === 'noSelectedDate'
      ? ''
      : toDate
        ? toDate
        : getDateFormat(new Date(), 'reservationDateFormat'),
  );

  let payloadData = {
    page: pageValue,
    pageSize: pageRowSize,
    status: window.location.pathname.includes('manage-invoice')
      ? 'charged'
      : status === 'all'
        ? ''
        : status,
    city: city,
    cityName: cityName,
    companyName: companyName,
    companyId: companyId,
    fromDate: dateFrom,
    toDate: dateTo,
    billingType: value,
    employeeId: employeeId,
  };

  const handleBillingList = (payload) => {
    if (payload?.city && payload?.companyId) {
      if (payload?.billingType === 'billToCreditCard') {
        if (!payload?.employeeId) {
          dispatch(clearBillingList());
          dispatch(clearFinalInvoice());
          return;
        }
        if (payload?.status === 'charged') {
          dispatch(
            getBillSelfConsolidatedInvoicesList({
              companyId: payload.companyId,
              cityId: payload.city,
              employeeId: payload.employeeId,
              page: Number(payload.page) || 1,
              pageSize: Number(payload.pageSize) || 10,
              dateFrom: payload.fromDate,
              dateTo: payload.toDate,
            }),
          );
        } else {
          dispatch(
            getBillSelfEligibleInvoices(
              payload.companyId,
              payload.city,
              payload.employeeId,
              Number(payload.page) || 1,
              Number(payload.pageSize) || 10,
              payload.fromDate,
              payload.toDate,
            ),
          );
        }
      } else {
        if (payload?.status === 'charged') {
          dispatch(getFinalInvoice({ ...payload, billingType: value }));
        } else {
          dispatch(getBillingList(payload));
        }
      }
    }
  };

  useEffect(() => {
    handleBillingList({ ...payloadData, billingType: value });
  }, []);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    setDateFrom(getDateFormat(new Date(), 'reservationDateFormat'));
    setDateTo(getDateFormat(new Date(), 'reservationDateFormat'));
    if (window.location.pathname.includes('manage-invoice')) {
      navigate(`/manage-invoice?invoiceTab=consolidated&billingType=${newValue}&status=charged`);
    } else {
      navigate(
        `/operation/billing/${newValue}?billingType=${newValue}&status=${newValue === 'billToCreditCard' ? 'charged' : 'all'}`,
      );
    }
  };

  const onSelectDateRange = (newValue) => {
    payloadData['fromDate'] = newValue ? getDateFormat(newValue[0], 'reservationDateFormat') : '';
    payloadData['toDate'] = newValue ? getDateFormat(newValue[1], 'reservationDateFormat') : '';

    if (newValue === null || newValue === 0) {
      setDateFrom('');
      setDateTo('');
      setSearchParams({
        ...params,
        fromDate: 'noSelectedDate',
        toDate: 'noSelectedDate',
      });
    } else {
      setDateFrom(getDateFormat(newValue[0], 'reservationDateFormat'));
      setDateTo(getDateFormat(newValue[1], 'reservationDateFormat'));
      setSearchParams({
        ...params,
        fromDate: getDateFormat(newValue[0], 'reservationDateFormat'),
        toDate: getDateFormat(newValue[1], 'reservationDateFormat'),
      });
    }
    handleBillingList(payloadData);
  };

  return (
    <Box className="billing-container">
      <Grid container>
        <Grid item className="tabContainer" xs={12}>
          <Box className="contentBoxStyle">
            <TabContext value={value}>
              <Grid
                container
                className="tabBoxStyle"
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                gap={0.5}
              >
                <Grid item>
                  <TabList
                    onChange={handleTabChange}
                    aria-label="lab API tabs example"
                    TabIndicatorProps={{
                      style: { backgroundColor: 'white' }, // Replace 'none' with your desired color
                    }}
                  >
                    {biillingTabs?.map((value, idx) => (
                      <Tab
                        key={idx}
                        label={value?.label}
                        value={value?.value}
                        className="tabStyle"
                      />
                    ))}
                  </TabList>
                </Grid>
                <Grid item>
                  <CommonSubHeader
                    billingSubHeader={true}
                    selectCity={false}
                    buttonName={''}
                    onSelectDateRange={onSelectDateRange}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                  />
                </Grid>
              </Grid>
              <Box className="mb20"></Box>
              {biillingTabs?.map((value, idx) => (
                <TabPanel value={value?.value} key={idx} className="vendotTable">
                  <BillingFlow
                    queryParams={params}
                    payloadData={payloadData}
                    tabValue={value}
                    handleBillingList={handleBillingList}
                  />
                </TabPanel>
              ))}
            </TabContext>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Billing;
